import { forwardRef, type InputHTMLAttributes } from "react";
import styled, { type DefaultTheme } from "styled-components";

import { useFieldControl } from "../Field/FieldContext";
import { controlTransition } from "../foundations/controlStyles";
import {
  interactiveSurfaceCss,
  type InteractiveSurfaceSpec,
} from "../foundations/interactiveSurface";

const Wrapper = styled.div`
  display: inline-flex;
`;

/**
 * Switch anatomy 를 상태 3-슬롯에 싣는다 — 면은 트랙, 테두리는 윤곽, 전경은 thumb(`::before` 가
 * `currentColor` 로 받는다). 켜짐은 별도 슬롯이 아니라 **resolver 입력**이라, ON/OFF 가 바꾸는 것이
 * 트랙 면과 thumb 잉크(무채↔유채)라는 계약이 이 함수 모양에 그대로 드러난다 — `border` 와
 * 비활성 잉크는 `checked` 를 참조하지 않는다.
 *
 * 색 축의 설계 근거(윤곽 잉크 밴드, 채도가 나르는 켜짐 신호, 존재감 사다리, 램프 진행 방향)는
 * RFC-semantic-token-map §`color.switch.*` 가 소유한다.
 */
function buildSwitchSurface(theme: DefaultTheme, checked: boolean): InteractiveSurfaceSpec {
  const { track, activeTrack, border, thumb, activeThumb } = theme.color.switch;
  const face = checked ? activeTrack : track;
  const ink = checked ? activeThumb : thumb;
  return {
    rest: { background: face.enabled, border: border.enabled, foreground: ink.enabled },
    hover: { background: face.hover, border: border.enabled, foreground: ink.hover },
    active: { background: face.pressed, border: border.enabled, foreground: ink.pressed },
    disabled: { background: face.disabled, border: border.disabled, foreground: thumb.disabled },
  };
}

// 기하만 여기서 소유한다. 셀렉터 구조·transition·focus ring·disabled 게이팅은
// `interactiveSurfaceCss` 가 단독으로 갖는다(component-authoring §5-C).
const Track = styled.input.attrs({ type: "checkbox" })`
  --_switch-height: 1.27em;
  --_switch-thumb-size: 0.8em;
  --_switch-thumb-inset: calc((var(--_switch-height) - var(--_switch-thumb-size)) / 2 - 1px);
  appearance: none;
  box-sizing: border-box;
  padding: 0;
  position: relative;
  border: 1px solid;
  border-radius: 1.25em;
  width: 2.25em;
  height: var(--_switch-height);
  flex-shrink: 0;

  ${({ checked = false, theme }) => interactiveSurfaceCss(buildSwitchSurface(theme, checked))}

  /* thumb — 색은 상속(currentColor)이라 상태 전개가 필요 없고, 여기서는 위치만 움직인다. */
  &::before {
    content: "";
    position: absolute;
    inset-block: 0;
    margin-block: auto;
    left: ${({ checked = false }) =>
      checked
        ? "calc(100% - var(--_switch-thumb-size) - var(--_switch-thumb-inset))"
        : "var(--_switch-thumb-inset)"};
    width: var(--_switch-thumb-size);
    height: var(--_switch-thumb-size);
    border-radius: 50%;
    background-color: currentColor;
    ${controlTransition("left")}
  }
`;

export interface SwitchProps extends Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "type" | "size" | "defaultChecked"
> {
  checked: boolean;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  disabled?: boolean;
}

/**
 * Controlled switch with native checkbox behavior and `role="switch"`.
 * 라벨이 필요하면 `<Field>`로 감싸세요 — 자체 `label` prop은 없습니다.
 */
export const Switch = forwardRef<HTMLInputElement, SwitchProps>(
  ({ checked, onChange, disabled = false, className, style, id, ...rest }, ref) => {
    const { controlId, disabled: isDisabled } = useFieldControl({
      componentName: "Switch",
      id,
      disabled,
    });

    return (
      <Wrapper className={className} style={style}>
        <Track
          ref={ref}
          role="switch"
          id={controlId}
          checked={checked}
          disabled={isDisabled}
          onChange={onChange}
          {...rest}
        />
      </Wrapper>
    );
  },
);

Switch.displayName = "Switch";
